import { useLog } from '@/features/shared/Hooks/useLog';
import { useMounted } from '@/hooks/useMounted';
import { useRequiredInterceptor } from '@/hooks/useRequiredInterceptor';
import { CMS_BASE_URL } from '@/utils/constants';
import { getSSOLocationURL } from '@/utils/helpers';
import { isPortalPathname, isProtectedPath } from '@/utils/portal';
import { User, UserManager, UserManagerSettings } from 'oidc-client-ts';
import React from 'react';
import { useOidcErrorInterceptor } from './useOidcErrorInterceptor';
import { useOidcHeaderInterceptor } from './useOidcHeaderInterceptor';
import { useOidcUserManager } from './useOidcUserManager';
import { useStorage } from '@/hooks/useStorage';
import { CALLBACK_REDIRECT_KEY } from '@/context/Oidc/constants';

const TRUSTED_DOMAINS = [`${CMS_BASE_URL}/restservices/portal/authorize`];

export type OidcProviderProps = {
  configuration: UserManagerSettings;
};

type OidcContextValue = {
  signIn: (args?: any) => Promise<void>;
  signOut: (args?: any) => Promise<void>;
  manager: UserManager | null;
  requireAuthentication: () => Promise<User | null>;
  isAuthenticated: boolean;
  removeSessionUser: () => Promise<void>;
} & Pick<ReturnType<typeof useOidcUserManager>, 'isLoading' | 'userData'>;

export const OidcContext = React.createContext<OidcContextValue>({
  signIn: () => Promise.resolve(),
  signOut: () => Promise.resolve(),
  manager: null,
  requireAuthentication: () => Promise.resolve(null),
  userData: null,
  isLoading: true,
  isAuthenticated: false,
  removeSessionUser: () => Promise.resolve(),
});

export const OidcProvider = ({
  configuration,
  children,
}: React.PropsWithChildren<OidcProviderProps>) => {
  const isMounted = useMounted();
  const [setCallbackRedirect] = useStorage<string>(sessionStorage, CALLBACK_REDIRECT_KEY);
  const { isReady, userData, isLoading, loadUserData, resetUserData, manager } =
    useOidcUserManager(configuration);

  const isAuthenticated = React.useMemo(() => Boolean(userData?.access_token), [userData]);
  const log = useLog({ context: 'OidcProvider' });

  /**
   * Sign In Redirect
   */
  const signIn = React.useCallback(
    async (args?: any) => {
      // Store callback redirect URL when inside the portal
      // Used in App.tsx
      if (isProtectedPath()) {
        setCallbackRedirect(getSSOLocationURL());
      }

      await manager.signinRedirect(args);
    },
    [manager, setCallbackRedirect],
  );

  /**
   * Sign Out Redirect
   */
  const signOut = React.useCallback(
    async (args?: any) => {
      resetUserData();

      const callbackRedirectURL = getSSOLocationURL({
        url: isPortalPathname(window.location.pathname) ? '' : window.location.pathname,
      });

      setCallbackRedirect(callbackRedirectURL);

      await manager.signoutRedirect(args);
    },
    [manager, resetUserData, setCallbackRedirect],
  );

  // Remove the current user session from the local storage
  const removeSessionUser = React.useCallback(async () => {
    await manager.removeUser();
  }, [manager]);

  /**
   * Handle Authentication Required from Interceptor
   */
  const handleAuthenticationRequired = React.useCallback(async () => {
    log('Authentication required');
    try {
      return await loadUserData();
    } catch (e) {
      log(e);
      signIn();
      return null;
    }
  }, [log, loadUserData, signIn]);

  /**
   * Intercept calls to specific URLs attaching accessToken in request body
   *
   * Should be called before other interceptors
   */
  useOidcHeaderInterceptor({
    accessToken: userData?.access_token,
    trustedDomains: TRUSTED_DOMAINS,
  });

  // Intercept authentication errors
  useOidcErrorInterceptor({
    isAuthenticated: isMounted && isAuthenticated,
    onRequired: handleAuthenticationRequired,
    trustedDomains: TRUSTED_DOMAINS,
  });

  // Intercept calls to specific URLs until authentication is loaded
  useRequiredInterceptor({
    isReady: isMounted && isAuthenticated,
    onRequired: handleAuthenticationRequired,
    trustedDomains: TRUSTED_DOMAINS,
    log,
  });

  const value = React.useMemo(() => {
    return {
      signIn,
      signOut,
      requireAuthentication: handleAuthenticationRequired,
      manager,
      userData,
      isLoading,
      isAuthenticated,
      removeSessionUser,
    };
  }, [
    signIn,
    signOut,
    manager,
    userData,
    isLoading,
    isAuthenticated,
    handleAuthenticationRequired,
    removeSessionUser,
  ]);

  return (
    <OidcContext.Provider value={value}>{isMounted && isReady && children}</OidcContext.Provider>
  );
};

export const PreviewOidcProvider = ({ children }: React.PropsWithChildren<any>) => (
  <OidcContext.Provider
    value={{
      signIn: () => Promise.resolve(),
      signOut: () => Promise.resolve(),
      manager: null,
      requireAuthentication: () => Promise.resolve(null),
      userData: null,
      isLoading: true,
      isAuthenticated: true,
      removeSessionUser: () => Promise.resolve(),
    }}
  >
    {children}
  </OidcContext.Provider>
);

export const useOidc = () => {
  const oidc = React.useContext(OidcContext);

  if (!oidc) {
    throw new Error('You need the OidcProvider');
  }

  return oidc;
};
